/* DAILE v2 (dv) — catalog «Salon», round 2: liquid glass for rounded tiles.
 *
 * The header glass (assets/dv-glass.js, [data-dv-glass]) builds its
 * refraction maps for a pill (radius = height / 2). The catalog room tiles and
 * the room-number medal are rounded rectangles, so this module does the same
 * optics for [data-dv-cw-glass] with a rounded-rectangle map: the corner
 * radius is read from the element's computed border-radius. Everything else is
 * shared with the header: the injected <span class="dv-glass-material">, its
 * CSS (dv-base.css: material gradient, backdrop blur, reflective lip) and the
 * filter pipeline (feDisplacementMap on the rim, frosted centre).
 *
 * Chromium only (as dv-glass.js): other browsers keep the CSS backdrop blur of
 * .dv-glass-material; reduced transparency / increased contrast get solid
 * glass (html.dv-opaque-glass is set by dv-glass.js; the media queries are
 * re-checked here too).
 *
 * data-dv-cw-glass="27" = frost stdDeviation (default 27).
 */
(() => {
  'use strict';

  if (!window.DV || typeof window.DV.register !== 'function') return;

  const doc = document;
  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = (name, attrs = {}) => {
    const node = doc.createElementNS(ns, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
    return node;
  };

  const reduceTransparency = matchMedia('(prefers-reduced-transparency: reduce)');
  const highContrast = matchMedia('(prefers-contrast: more)');
  const isOpaque = () => reduceTransparency.matches || highContrast.matches;

  const canRefract = /Chrome|Chromium|Edg\//.test(navigator.userAgent)
    && !!(window.CSS && CSS.supports && CSS.supports('backdrop-filter', 'url("#dv-cw-glass-probe")'))
    && 'ResizeObserver' in window;

  const rebuilders = new Set();
  const sync = () => rebuilders.forEach((rebuild) => rebuild(true));
  window.DV.onMediaChange(reduceTransparency, sync);
  window.DV.onMediaChange(highContrast, sync);

  let defs = null;
  const ensureDefs = () => {
    if (defs && defs.isConnected) return defs;
    const svg = svgEl('svg', { 'aria-hidden': 'true', focusable: 'false', width: 0, height: 0 });
    svg.classList.add('dv-glass-filter-defs');
    defs = svgEl('defs');
    svg.append(defs);
    doc.body.append(svg);
    return defs;
  };

  let counter = 0;

  // Outward normal and inside depth of a rounded rectangle (w × h, radius r)
  // at pixel centre (x + .5, y + .5).
  const roundedRect = (x, y, w, h, r) => {
    const px = x + 0.5 - w / 2;
    const py = y + 0.5 - h / 2;
    const qx = Math.abs(px) - (w / 2 - r);
    const qy = Math.abs(py) - (h / 2 - r);
    const sx = px < 0 ? -1 : 1;
    const sy = py < 0 ? -1 : 1;
    if (qx > 0 && qy > 0) {
      const d = Math.hypot(qx, qy);
      return { depth: r - d, nx: d ? (qx / d) * sx : 0, ny: d ? (qy / d) * sy : 0 };
    }
    if (qx > qy) return { depth: r - qx, nx: sx, ny: 0 };
    return { depth: r - qy, nx: 0, ny: sy };
  };

  const mount = (target) => {
    let material = [...target.children].find((child) => child.classList.contains('dv-glass-material'));
    const injected = !material;
    if (injected) {
      material = doc.createElement('span');
      material.className = 'dv-glass-material';
      material.setAttribute('aria-hidden', 'true');
      target.prepend(material);
    }
    target.classList.add('dv-cw-glass-mounted');

    const unmount = () => {
      target.classList.remove('dv-cw-glass-mounted');
      if (injected) material.remove();
      else material.style.removeProperty('--dv-optical-filter');
    };

    if (!canRefract) return unmount;

    const id = `dv-cw-optics-${++counter}`;
    const frost = Number.parseFloat(target.getAttribute('data-dv-cw-glass')) || 27;
    const filter = svgEl('filter', { id, x: 0, y: 0, filterUnits: 'userSpaceOnUse', 'color-interpolation-filters': 'sRGB' });
    const vector = svgEl('feImage', { x: 0, y: 0, preserveAspectRatio: 'none', result: 'vector' });
    const rim = svgEl('feImage', { x: 0, y: 0, preserveAspectRatio: 'none', result: 'rim' });
    filter.append(
      vector,
      svgEl('feDisplacementMap', { in: 'SourceGraphic', in2: 'vector', scale: 28, xChannelSelector: 'R', yChannelSelector: 'G', result: 'refracted' }),
      svgEl('feGaussianBlur', { in: 'refracted', stdDeviation: 0.65, result: 'polished' }),
      rim,
      svgEl('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: frost, result: 'frosted' }),
      svgEl('feComposite', { in: 'polished', in2: 'rim', operator: 'in', result: 'edge' }),
      svgEl('feComposite', { in: 'frosted', in2: 'rim', operator: 'out', result: 'centre' }),
    );
    const merge = svgEl('feMerge');
    merge.append(svgEl('feMergeNode', { in: 'centre' }), svgEl('feMergeNode', { in: 'edge' }));
    filter.append(merge);
    ensureDefs().append(filter);

    let previous = '';
    const rebuild = (force) => {
      if (isOpaque()) {
        material.style.removeProperty('--dv-optical-filter');
        previous = '';
        return;
      }
      const w = Math.round(target.clientWidth);
      const h = Math.round(target.clientHeight);
      const radius = Math.min(
        Number.parseFloat(getComputedStyle(target).borderTopLeftRadius) || 0,
        w / 2,
        h / 2,
      );
      const key = `${w}:${h}:${radius}`;
      if (!w || !h || (!force && key === previous)) return;
      previous = key;
      const canvas = doc.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const context = canvas.getContext('2d');
      if (!context) return;
      const displacement = context.createImageData(w, h);
      const edge = context.createImageData(w, h);
      // The refracting lip hugs the whole outline: at least 10px, at most 14px.
      const ring = Math.max(10, Math.min(14, Math.min(w, h) * 0.2));
      const r = Math.max(radius, 1);
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const { depth, nx, ny } = roundedRect(x, y, w, h, r);
          const t = Math.max(0, Math.min(1, depth / ring));
          const weight = depth >= 0 ? (1 - t) ** 2 : 0;
          const i = (y * w + x) * 4;
          displacement.data[i] = 128 - nx * 124 * weight;
          displacement.data[i + 1] = 128 - ny * 124 * weight;
          displacement.data[i + 2] = 128;
          displacement.data[i + 3] = 255;
          const blend = Math.max(0, Math.min(1, (t - 0.38) / 0.62));
          edge.data[i] = 255;
          edge.data[i + 1] = 255;
          edge.data[i + 2] = 255;
          edge.data[i + 3] = depth >= 0 ? Math.round(255 * (1 - blend * blend * (3 - 2 * blend))) : 0;
        }
      }
      context.putImageData(displacement, 0, 0);
      vector.setAttribute('href', canvas.toDataURL());
      context.putImageData(edge, 0, 0);
      rim.setAttribute('href', canvas.toDataURL());
      [filter, vector, rim].forEach((node) => {
        node.setAttribute('width', w);
        node.setAttribute('height', h);
      });
      material.style.setProperty('--dv-optical-filter', `url("#${id}")`);
    };

    const observer = new ResizeObserver(() => rebuild(false));
    observer.observe(target);
    rebuild(false);
    rebuilders.add(rebuild);

    return () => {
      observer.disconnect();
      rebuilders.delete(rebuild);
      filter.remove();
      unmount();
    };
  };

  window.DV.register('dv-catalog-glass', (root) => {
    const cleanups = window.DV.queryAll(root, '[data-dv-cw-glass]').map(mount);
    if (!cleanups.length) return undefined;
    return () => cleanups.forEach((fn) => fn());
  });
})();
